import { useDispatch, useSelector } from "react-redux";
import {
  setViewportState,
  toggleZoomState,
  selectViewportState,
  ViewportState,
  ZoomState,
  selectZoomState,
} from "../features/ui/uiSlice";
import { GraphDirection, SideView } from "./viewport/SideView";
import { CrossSectionView } from "./viewport/CrossSectionView";
import { Panel } from "./viewport/Panel";
import React from "react";
import { hasInteractionDiagramData, InteractionDiagram, isSlsLcombSelection } from "./viewport/InteractionDiagram";
import { DiagramResults } from "./viewport/drawSectionForceDiagrams";
import { useProject } from "../features/projects/projectSlice";
import { ng } from "@common/ngds";
import { findSectionInProject } from "./viewport/shared";
import "../styling/viewport/viewport.css";
import { SXSpin } from "@common/components/ui-components/SXSpin";
import { useTranslation } from "react-i18next";
import { View3D } from "./viewport/View3D";

export type GraphPointsMode = "off" | "max" | "all";

interface ViewportProps {
  results: DiagramResults;
  selectedLcombGuid: string;
  graphPointsMode: GraphPointsMode;
  orderResultsLabel?: string;
}

interface IPanelContext {
  zoomAll: boolean;
  hasInteraction: boolean;
  toggle: (d: ZoomState) => void;
  results: DiagramResults;
  selectedLcombGuid: string;
  graphPointsMode: GraphPointsMode;
  globalZoom: ZoomState;
  orderResultsLabel?: string;
}
const PanelContext = React.createContext<IPanelContext | null>(null);
const usePanelContext = (): IPanelContext => {
  const pan = React.useContext(PanelContext);
  if (pan === null) {
    throw "Invalid use of panel context";
  }
  return pan;
};

const SidePanel = (props: { viewDirection: GraphDirection; display: ZoomState; cellClass: string }) => {
  const ctx = usePanelContext();
  const shouldDisplay = ctx.zoomAll || ctx.globalZoom == props.display;
  const { t, i18n } = useTranslation();

  return (
    shouldDisplay && (
      <div className={`viewport-grid__cell ${props.cellClass}`}>
        <Panel
          id="sideview"
          label={props.viewDirection === "xy" ? t("XY_view_.label") : t("XZ_view_.label")}
          secondaryLabel={ctx.orderResultsLabel}
          toggle={() => ctx.toggle(props.display)}
          isFullscreen={!ctx.zoomAll}
        >
          <SideView
            viewDirection={props.viewDirection}
            results={ctx.results}
            selectedLcombGuid={ctx.selectedLcombGuid}
            graphPointsMode={ctx.graphPointsMode}
          />
        </Panel>
      </div>
    )
  );
};

const InteractionPanel = (props: { viewDirection: GraphDirection; display: ZoomState; cellClass: string }) => {
  const ctx = usePanelContext();
  const shouldDisplay = ctx.hasInteraction && (ctx.zoomAll || ctx.globalZoom == props.display);
  const { t, i18n } = useTranslation();

  return (
    shouldDisplay && (
      <div className={`viewport-grid__cell ${props.cellClass}`}>
        <Panel
          id="interactiondiagram"
          label={
            // Named like the printout's caption (ms-structural/sx-column#407): the plane, then the moment.
            props.viewDirection === "xy" ? (
              <>
                {t("interaction_diagram_XY_.label")} (M<sub>z</sub>)
              </>
            ) : (
              <>
                {t("interaction_diagram_XZ_.label")} (M<sub>y</sub>)
              </>
            )
          }
          toggle={() => ctx.toggle(props.display)}
          isFullscreen={!ctx.zoomAll}
        >
          <InteractionDiagram
            alternate={props.viewDirection === "xy"}
            results={ctx.results}
            selectedLcombGuid={ctx.selectedLcombGuid}
          />
        </Panel>
      </div>
    )
  );
};

const CrossSectionPanel = (props: {
  viewDirection: ViewportState;
  setViewportState: (s: ViewportState) => void;
  cellClass: string;
}) => {
  const ctx = usePanelContext();
  const shouldDisplay = ctx.zoomAll || ctx.globalZoom == "cross";
  const { t, i18n } = useTranslation();

  return (
    shouldDisplay && (
      <div className={`viewport-grid__cell ${props.cellClass}`}>
        <Panel
          id="crosssectionview"
          label={t("cross_section_view_.label")}
          toggle={() => ctx.toggle("cross")}
          isFullscreen={!ctx.zoomAll}
        >
          <CrossSectionView
            a_selected={props.viewDirection === "xy"}
            b_selected={props.viewDirection === "xz"}
            select_a={() => {
              props.setViewportState("xy");
            }}
            select_b={() => {
              props.setViewportState("xz");
            }}
          />
        </Panel>
      </div>
    )
  );
};

const View3DPanel = (props: { cellClass: string }) => {
  const ctx = usePanelContext();
  const shouldDisplay = ctx.zoomAll || ctx.globalZoom == "side";
  const { t } = useTranslation();

  return (
    shouldDisplay && (
      <div className={`viewport-grid__cell ${props.cellClass}`}>
        <Panel id="view3d" label={t("isometric_view_.label")} toggle={() => ctx.toggle("side")} isFullscreen={!ctx.zoomAll}>
          <View3D
            results={ctx.results}
            selectedLcombGuid={ctx.selectedLcombGuid}
            graphPointsMode={ctx.graphPointsMode}
          />
        </Panel>
      </div>
    )
  );
};

export const Viewport = (props: ViewportProps) => {
  const dispatch = useDispatch();
  const viewDirection = useSelector(selectViewportState);
  const { project: projectSource } = useProject();
  const dataJson = projectSource?.data;
  const data = dataJson ? ng.frontend.column.Project.fromObject(dataJson) : null;
  const section = findSectionInProject(data);

  const zoom = useSelector(selectZoomState);
  const toggle = React.useCallback(
    (d: ZoomState) => {
      dispatch(toggleZoomState(d));
    },
    [dispatch],
  );

  // There is no interaction check in the serviceability limit state, so an SLS selection has no
  // diagram at all — not even a maximized panel may stand in for one.
  const slsSelection = isSlsLcombSelection(dataJson, props.selectedLcombGuid);
  const interactionZoom = zoom === "interaction" || zoom === "interaction-split";

  // interaction diagram is only available for concrete, and only once a code check has produced
  // curves to draw — an empty diagram with nothing but axes is not worth a panel
  // ... or if we have maximized it
  const hasInteraction =
    !slsSelection &&
    ((section.sec?.materialCategory === ng.sections.MaterialCategory.MATERIAL_CATEGORY_CONCRETE &&
      hasInteractionDiagramData(dataJson, props.selectedLcombGuid)) ||
      interactionZoom);

  // Maximizing a panel hides every other one, so a hidden interaction panel that is the maximized
  // one would leave the viewport empty — switching to an SLS combination falls back to showing all.
  const effectiveZoom: ZoomState = interactionZoom && !hasInteraction ? "all" : zoom;

  const zoomAll = effectiveZoom == "all";

  const panelContext = React.useMemo(
    (): IPanelContext => ({
      results: props.results,
      hasInteraction,
      globalZoom: effectiveZoom,
      zoomAll,
      toggle,
      selectedLcombGuid: props.selectedLcombGuid,
      graphPointsMode: props.graphPointsMode,
      orderResultsLabel: props.orderResultsLabel,
    }),
    [
      props.results,
      hasInteraction,
      effectiveZoom,
      zoomAll,
      toggle,
      props.selectedLcombGuid,
      props.graphPointsMode,
      props.orderResultsLabel,
    ],
  );

  const viewPortGridClass = "viewport-grid";
  const viewportGridMaximized = "viewport-grid--maximized";

  const gridClass = [
    viewPortGridClass,
    viewDirection === "xy+xz" ? "viewport-grid--split" : "viewport-grid--single",
    hasInteraction ? "viewport-grid--has-interaction" : "",
    !zoomAll ? viewportGridMaximized : "",
  ].join(" ");

  const view3dGridClass = [viewPortGridClass, "viewport-grid--single", !zoomAll ? viewportGridMaximized : ""].join(" ");

  if (!section.hasData) {
    return (
      <div id="main-content-area">
        <div className={`${viewPortGridClass} ${viewportGridMaximized}`}>
          <SXSpin center />
        </div>
      </div>
    );
  }

  return (
    <PanelContext value={panelContext}>
      <div id="main-content-area">
        {viewDirection === "3d" && (
          <div className={view3dGridClass}>
            <CrossSectionPanel
              setViewportState={(s) => dispatch(setViewportState(s))}
              viewDirection={viewDirection}
              cellClass="viewport-grid__cell--cross"
            />
            <View3DPanel cellClass="viewport-grid__cell--side-1" />
          </div>
        )}
        {viewDirection === "xy+xz" && (
          <div className={gridClass}>
            <CrossSectionPanel
              setViewportState={(s) => dispatch(setViewportState(s))}
              viewDirection={viewDirection}
              cellClass="viewport-grid__cell--cross"
            />
            <SidePanel display="side" viewDirection="xy" cellClass="viewport-grid__cell--side-1" />
            <InteractionPanel display="interaction" viewDirection="xy" cellClass="viewport-grid__cell--inter-1" />
            <SidePanel display="side-split" viewDirection="xz" cellClass="viewport-grid__cell--side-2" />
            <InteractionPanel display="interaction-split" viewDirection="xz" cellClass="viewport-grid__cell--inter-2" />
          </div>
        )}
        {viewDirection !== "xy+xz" && viewDirection !== "3d" && (
          <div className={gridClass}>
            <CrossSectionPanel
              setViewportState={(s) => dispatch(setViewportState(s))}
              viewDirection={viewDirection}
              cellClass="viewport-grid__cell--cross"
            />
            <SidePanel display="side" viewDirection={viewDirection} cellClass="viewport-grid__cell--side-1" />
            <InteractionPanel
              display="interaction"
              viewDirection={viewDirection}
              cellClass="viewport-grid__cell--inter-1"
            />
          </div>
        )}
      </div>
    </PanelContext>
  );
};
